import { supabase } from '../lib/supabase';

// ============================================
// AGENCY MANAGEMENT (Super Admin)
// ============================================

/**
 * Get all agencies from user_profiles where role = agency_admin
 */
export async function getAgencies() {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('role', 'agency_admin')
      ?.order('created_at', { ascending: false });

    if (error) {
      console.error('Get agencies error:', error?.message);
      // Return empty array if table doesn't exist yet
      return [];
    }

    return (data || [])?.map((profile) => ({
      id: profile?.id,
      agencyName: profile?.agency || profile?.name || '',
      email: profile?.email || '',
      contactName: profile?.name || '',
      phone: profile?.phone || '',
      city: profile?.city || '',
      subscription: profile?.subscription || 'standard',
      status: profile?.status || 'active',
      createdAt: profile?.created_at,
      avatar: profile?.avatar,
    }));
  } catch (err) {
    console.error('Get agencies error:', err);
    return [];
  }
}

/**
 * Create a new agency account using Supabase Auth signUp
 * This creates both the auth user and the profile
 */
export async function createAgencyAccount(formData) {
  const { agencyName, email, password, contactName, phone, city, subscription, status } = formData;

  // Step 1: Create the auth user via signUp
  const { data: authData, error: authError } = await supabase?.auth?.signUp({
    email,
    password,
    options: {
      data: {
        full_name: contactName,
        agency_name: agencyName,
        role: 'agency_admin',
      },
    },
  });

  if (authError) {
    throw new Error(authError?.message || 'Erreur lors de la création du compte');
  }

  const userId = authData?.user?.id;
  if (!userId) {
    throw new Error('Impossible de créer le compte utilisateur');
  }

  // Step 2: Upsert the user profile with agency details
  const { error: profileError } = await supabase?.from('user_profiles')?.upsert({
    id: userId,
    email,
    name: contactName,
    role: 'agency_admin',
    agency: agencyName,
    phone: phone || null,
    city: city || null,
    subscription: subscription || 'standard',
    status: status || 'active',
    created_at: new Date()?.toISOString(),
    updated_at: new Date()?.toISOString(),
  });

  if (profileError) {
    console.error('Profile upsert error:', profileError?.message);
    // Don't throw — auth user was created, profile might be created by trigger
  }

  return { userId, email };
}

/**
 * Update an existing agency's profile data
 */
export async function updateAgencyAccount(userId, formData) {
  const { agencyName, contactName, phone, city, subscription, status, password } = formData;

  // Update profile
  const { error: profileError } = await supabase
    ?.from('user_profiles')
    ?.update({
      name: contactName,
      agency: agencyName,
      phone: phone || null,
      city: city || null,
      subscription: subscription || 'standard',
      status: status || 'active',
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', userId);

  if (profileError) {
    throw new Error(profileError?.message || 'Erreur lors de la mise à jour');
  }

  // If password provided, update it via admin API (requires service role)
  // For now we skip password update since we only have anon key
  // Password reset can be done via email

  return { success: true };
}

/**
 * Delete an agency account (soft delete by setting status to inactive)
 * Full deletion requires service role key — we do soft delete here
 */
export async function deleteAgencyAccount(userId) {
  const { error } = await supabase
    ?.from('user_profiles')
    ?.update({
      status: 'inactive',
      updated_at: new Date()?.toISOString(),
    })
    ?.eq('id', userId);

  if (error) {
    throw new Error(error?.message || 'Erreur lors de la suppression');
  }

  return { success: true };
}
